import { routes } from "@constants/routes";
import { GraduatedIcon, LinkIcon, useFetchFile, UserIcon, UserTypeEnum } from "@cpj-ai/panel-npm-package";
import { JobSeekerType } from "@graphql/generated";
import { Avatar, Button, Card, CardBody, Chip } from "@heroui/react";
import { withType } from "@utils/typeGuards";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";
import { TJobSeeker } from ".";
interface CandidateInfoPanelProps {
  jobSeeker?: TJobSeeker | null;
  jobPositionId?: string;
  jobPositionAssignmentId?: string;
}

const CandidateInfoPanel: React.FC<CandidateInfoPanelProps> = ({
  jobSeeker,
  jobPositionId,
  jobPositionAssignmentId,
}) => {
  const navigate = useNavigate();
  const file = withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
    return jobSeeker?.profile?.avatar?.file;
  });
  const { data: fileData } = useFetchFile({
    urlPath: file ? String(file) : undefined,
    userType: UserTypeEnum.ORG,
  });

  const getUniversityName = () => {
    return withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
      return jobSeeker?.educations && jobSeeker?.educations[0] && jobSeeker?.educations[0].university
        ? jobSeeker?.educations?.[0]?.university?.name
        : "-";
    });
  };

  const getDegreeAndField = () => {
    return withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
      if (!jobSeeker?.educations || !jobSeeker?.educations[0]) return "-";

      const education = jobSeeker?.educations[0];
      const degree = education.degree || "";
      const fieldName = education.field && education.field.name ? education.field.name : "";

      return `${degree} of ${fieldName}`;
    });
  };

  const getGraduationYear = () => {
    return withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
      if (!jobSeeker?.educations || !jobSeeker?.educations[0] || !jobSeeker?.educations[0].end) {
        return "-";
      }

      return new Date(jobSeeker?.educations[0].end).getFullYear();
    });
  };

  const phoneNumber = withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
    return jobSeeker?.profile?.contacts?.find((contact) => contact?.type === "PHONE")?.value;
  });

  const address = withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
    return jobSeeker?.profile?.contacts?.find((contact) => contact?.type === "ADDRESS")?.value;
  });

  const handleViewProfile = () => {
    navigate(
      routes.jobPositions.candidate
        .replace(":id", jobPositionId ?? "")
        .replace(":assignmentId", jobPositionAssignmentId ?? ""),
    );
  };

  return (
    <div className="w-full md:w-1/3 border-r p-4 md:p-6 bg-gradient-to-b from-gray-50 to-white">
      <div className="flex flex-col items-center mb-6">
        <div className="relative mb-3">
          <Avatar
            src={fileData}
            className="w-24 h-24 border-4 border-white shadow-md"
            classNames={{ img: "opacity-100" }}
            radius="full"
          />
          <div className="absolute -bottom-2 -right-2 bg-primary-100 text-primary-600 rounded-full px-2 py-1 text-xs font-semibold border border-primary-200">
            50%
          </div>
        </div>

        <h3 className="text-xl font-semibold text-gray-800">
          {withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
            return `${jobSeeker?.firstName} ${jobSeeker?.lastName}`;
          })}
        </h3>

        <div className="flex items-center gap-2 mt-1">
          <Chip color="warning" variant="dot" size="sm" className="shadow-sm">
            {withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
              return `${jobSeeker?.profile?.score} Score`;
            })}
          </Chip>
        </div>

        <Button
          variant="bordered"
          color="primary"
          size="sm"
          radius="lg"
          className="mt-4 shadow-sm hover:shadow transition-all duration-200"
          startContent={<LinkIcon className="stroke-primary-500 w-5 h-5" />}
          onPress={handleViewProfile}
        >
          View Profile
        </Button>
      </div>

      <Card className="mb-4 shadow-sm border-none bg-white">
        <CardBody className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-blue-50">
              <UserIcon className="w-5 h-5 stroke-blue-500" />
            </div>
            <h4 className="text-sm font-semibold text-gray-700 uppercase">Personal Information</h4>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Email</span>
              <span className="text-blue-500 font-medium">
                {withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
                  return jobSeeker?.email;
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Phone</span>
              <span className="font-medium">{phoneNumber}</span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Gender</span>
              <span className="font-medium capitalize">
                {withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
                  return jobSeeker?.profile?.gender?.toLowerCase();
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Birthdate</span>
              <span className="font-medium">
                {withType<JobSeekerType>(jobSeeker, "JobSeekerType", (jobSeeker) => {
                  return jobSeeker?.profile?.birthDate
                    ? dayjs(jobSeeker?.profile?.birthDate).format("MMM D, YYYY")
                    : "N/A";
                })}
              </span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Address</span>
              <span className="font-medium">{address}</span>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card className="shadow-sm border-none bg-white">
        <CardBody className="p-4">
          <div className="flex items-center gap-2 mb-3">
            <div className="p-1.5 rounded-md bg-green-50 text-green-500">
              <GraduatedIcon className="w-5 h-5 stroke-green-500" />
            </div>
            <h4 className="text-sm font-semibold text-gray-700 uppercase">Education Information</h4>
          </div>

          <div className="space-y-3">
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">University</span>
              <span className="font-medium">{getUniversityName()}</span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Qualification</span>
              <span className="font-medium">{getDegreeAndField()}</span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Year Graduation</span>
              <span className="font-medium">{getGraduationYear()}</span>
            </div>
            <div className="flex items-center gap-2 p-2 hover:bg-gray-50 rounded-md transition-colors">
              <span className="text-gray-500 w-24 text-sm">Referral</span>
              <span className="text-gray-400 italic">Not Provided</span>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};

export default CandidateInfoPanel;
